import { formatPeopleCount } from '~/utils/helpers';

import { COMPACT_NUMBER_THRESHOLD, MAX_CATEGORY_TICKS, ROTATION_LABEL_LENGTH } from './chartConfig';

/** Abbreviates numbers above COMPACT_NUMBER_THRESHOLD to '1.1K', '45K' etc. @see docs/ai-insights-chart-rendering.md §1 */
export const formatCompactNumber = (value: number): string => {
  if (Math.abs(value) > COMPACT_NUMBER_THRESHOLD) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    }).format(value);
  }
  return formatPeopleCount(value);
};

/**
 * Returns a thinned array of tick values for nivo axes so at most `maxTicks` labels render.
 * Always includes first and last label. Deduplicates by label string (§7).
 * @see docs/ai-insights-chart-rendering.md §2, §7
 */
export const getCategoryTickValues = (labels: string[], maxTicks = MAX_CATEGORY_TICKS): string[] => {
  if (labels.length <= maxTicks) return labels;
  const step = Math.ceil(labels.length / maxTicks);
  const indices: number[] = [];
  for (let i = 0; i < labels.length; i += step) {
    indices.push(i);
  }
  const lastIndex = labels.length - 1;
  const lastTickIndex = indices[indices.length - 1];
  if (lastTickIndex !== lastIndex) {
    if (lastIndex - lastTickIndex <= step / 2) {
      indices[indices.length - 1] = lastIndex;
    } else {
      indices.push(lastIndex);
    }
  }
  // Deduplicate: if two positions share the same label, nivo would show it on both.
  // Keep first occurrence of each label string.
  const seen = new Set<string>();
  return indices.reduce<string[]>((acc, i) => {
    const label = labels[i];
    if (!seen.has(label)) {
      seen.add(label);
      acc.push(label);
    }
    return acc;
  }, []);
};

/**
 * Returns -45 (diagonal) if any label exceeds ROTATION_LABEL_LENGTH characters, else 0.
 * @see docs/ai-insights-chart-rendering.md §3
 */
export const getCategoryTickRotation = (labels: string[]): number => {
  return labels.some((l) => l.length > ROTATION_LABEL_LENGTH) ? -45 : 0;
};
